/**
 * Editability decision for the Preferences > General > Profile card.
 *
 * Regression guard (docs/REGRESSIONS.md 2026-07-01): an admin IMPERSONATING
 * another user must be able to manage THAT user's Profile — set the photo and
 * edit First name / Last name / Personal phone. The backend writes already
 * target the impersonated user: the impersonation middleware swaps
 * `userEmail` -> the impersonated user's email for `/api/hr/*`, and both
 * `PUT /api/hr/profile` and `POST /api/hr/profile/photo` resolve the target by
 * that swapped email. A hard "Disabled while impersonating" lock on the whole
 * card is therefore a UX regression, not a safety control.
 *
 * Invariants encoded here:
 *  - A normal (non-impersonating) user keeps the existing self-edit behavior:
 *    name / phone / photo editable.
 *  - While impersonating, name / phone / photo become editable ONLY once the
 *    impersonated user's HR profile has loaded — that record is both the read
 *    source (name/phone/photo values) and the write target (HR "Save" bar ->
 *    PUT /api/hr/profile; photo -> POST /api/hr/profile/photo). Enabling before
 *    it loads would let an edit target the wrong / an empty record.
 *  - Email is the identity key and is NEVER editable from this card.
 */
export type ProfileEditability = {
  /** First name / Last name / Personal phone inputs editable */
  nameEditable: boolean
  /** "Change photo" / "Add photo" button + file input enabled */
  photoEditable: boolean
  /** Email is the identity key — never editable from this card */
  emailEditable: false
}

export function profileCardEditability(opts: {
  /** true when an admin is acting as another user (impersonation active) */
  impersonating: boolean
  /** true once the impersonated user's HR profile has loaded */
  hrProfileLoaded: boolean
}): ProfileEditability {
  const { impersonating, hrProfileLoaded } = opts
  // Self-edit: always editable. Impersonating: editable once the impersonated
  // user's HR profile is loaded (that is the record edits read from and write
  // to). Email is the identity key and stays read-only in every mode.
  const editable = impersonating ? hrProfileLoaded : true
  return { nameEditable: editable, photoEditable: editable, emailEditable: false }
}

// NOTE: the nav-avatar photo precedence (`impersonatedNavAvatarPhoto`) that
// used to live here was SUPERSEDED by `navAvatarSource` in
// `src/lib/identity/avatarSource.ts` — the single precedence source for the
// nav trigger / dropdown / impersonate-picker avatars (hrProfile → versioned
// /api/me photo → prefsPhoto(self-only) → by-email+ver → initials). Its
// impersonation invariants (hrProfile wins; the admin's prefsPhoto NEVER
// renders while impersonating) are re-encoded and tested there. Keep exactly
// ONE precedence source — do not re-add a second helper here.
